import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { useToast } from "../hooks/use-toast";
import { getGeoLocation, submitLead } from "../utils/api";
import { Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const ContactForm = ({
  packageTitle = "",
  packageAmount = 0,
  showPackageInfo = false,
  onSuccess,
  isCompact = false,
  submitText = "Submit Request",
}) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    countryCode: "+1",
    phone: "",
    description: "",
  });
  const [errors, setErrors] = useState({});

  // Country codes list
  const countryCodes = [
    { code: "+1", country: "US/Canada" },
    { code: "+44", country: "UK" },
    { code: "+91", country: "India" },
    { code: "+92", country: "Pakistan" },
    { code: "+61", country: "Australia" },
    { code: "+86", country: "China" },
    { code: "+81", country: "Japan" },
    { code: "+49", country: "Germany" },
    { code: "+33", country: "France" },
    { code: "+39", country: "Italy" },
    { code: "+34", country: "Spain" },
    { code: "+971", country: "UAE" },
    { code: "+966", country: "Saudi Arabia" },
    { code: "+65", country: "Singapore" },
    { code: "+31", country: "Netherlands" },
  ];

  useEffect(() => {
    // Fetch geolocation on component mount
    const fetchGeoData = async () => {
      const data = await getGeoLocation();
      if (data) {
        setGeoData(data);
      }
    };
    fetchGeoData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Special handling for phone number - auto format
    if (name === "phone") {
      // Remove all non-digit characters
      const cleaned = value.replace(/\D/g, "");
      // Limit to 10 digits
      const limited = cleaned.slice(0, 10);

      // Format as user types: 123-456-7890
      let formatted = limited;
      if (limited.length > 6) {
        formatted = `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`;
      } else if (limited.length > 3) {
        formatted = `${limited.slice(0, 3)}-${limited.slice(3)}`;
      }

      setFormData({
        ...formData,
        [name]: formatted,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleCountryCodeChange = (value) => {
    setFormData({
      ...formData,
      countryCode: value,
    });
  };

  const validateEmail = (email) => {
    // More comprehensive email validation
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Remove all non-digit characters
    const cleanPhone = phone.replace(/\D/g, "");
    // Check if it's exactly 10 digits
    return cleanPhone.length === 10;
  };

  const formatPhoneExample = () => {
    // Show example based on selected country code
    const exampleNumbers = {
      "+1": "+1 123-456-7890",
      "+44": "+44 123-456-7890",
      "+91": "+91 123-456-7890",
      "+92": "+92 123-456-7890",
    };
    return (
      exampleNumbers[formData.countryCode] ||
      `${formData.countryCode} 123-456-7890`
    );
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email =
        "Please enter a valid email format (e.g., user@example.com)";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = `Please enter a valid phone number. Format: ${formatPhoneExample()}`;
    }

    if (!formData.description.trim()) {
      newErrors.description = "Please tell us how we can help you";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Prepare form data with proper structure
      const submitData = {
        name: formData.name,
        email: formData.email,
        countryCode: parseInt(formData.countryCode.replace("+", "")),
        country: geoData?.data?.countryCode || "US",
        phoneNumber: formData.phone.replace(/\D/g, ""), // Only numeric digits
        description: formData.description,
      };

      // Submit lead using centralized API
      const result = await submitLead(
        submitData,
        geoData,
        packageTitle,
        packageAmount,
      );

      // Handle 200 Success
      if (result.success && result.statusCode === 200 && result.customerId) {
        toast({
          title: "Success!",
          description:
            "Your request has been submitted successfully. Redirecting...",
          variant: "default",
        });

        // Reset form
        setFormData({
          name: "",
          email: "",
          countryCode: "+1",
          phone: "",
          description: "",
        });

        // Call onSuccess callback if provided (for modal)
        if (onSuccess) {
          onSuccess();
        }

        // Redirect to application page with customerId
        setTimeout(() => {
          window.location.href = `/thanks?customerid=${result.customerId}`;
        }, 1500);
      }
      // Handle 401 Unauthorized
      else if (result.statusCode === 401) {
        toast({
          title: "Unauthorized",
          description: result.message,
          variant: "destructive",
        });
      }
      // Handle 400 Bad Request
      else if (result.statusCode === 400) {
        toast({
          title: "Validation Error",
          description: result.message,
          variant: "destructive",
        });
      }
      // Handle other errors
      else {
        toast({
          title: "Error",
          description:
            result.message || "Something went wrong. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast({
        title: "Error",
        description: "Failed to submit form. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={isCompact ? "space-y-4" : "space-y-6"}
    >
      {showPackageInfo && packageTitle && (
        <div className="bg-[#FDFEFE] p-4 rounded-lg border border-[#C0392B]/20">
          <p className="text-sm font-medium text-[#2C3E50]">
            Selected Package:{" "}
            <span className="text-[#C0392B]">{packageTitle}</span>
          </p>
          {packageAmount > 0 && (
            <p className="text-sm text-[#7F8C8D] mt-1">
              Amount: ${packageAmount}
            </p>
          )}
        </div>
      )}

      <div
        className={`grid grid-cols-1 ${isCompact ? "gap-4" : "md:grid-cols-2 gap-6"}`}
      >
        <div className="space-y-2">
          <Label htmlFor="name" className="text-[#2C3E50]">
            Full Name *
          </Label>
          <Input
            id="name"
            name="name"
            type="text"
            placeholder="John Doe"
            value={formData.name}
            onChange={handleChange}
            className={`border-[#7F8C8D]/30 focus:border-[#C0392B] ${errors.name ? "border-red-500" : ""}`}
          />
          {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-[#2C3E50]">
            Email Address *
          </Label>
          <div className="relative">
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="user@example.com"
              value={formData.email}
              onChange={handleChange}
              className={`border-[#7F8C8D]/30 focus:border-[#C0392B] ${
                errors.email
                  ? "border-red-500"
                  : formData.email && validateEmail(formData.email)
                    ? "border-green-500"
                    : ""
              }`}
            />
            {formData.email &&
              validateEmail(formData.email) &&
              !errors.email && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500">
                  ✓
                </span>
              )}
          </div>
          {errors.email && (
            <p className="text-red-500 text-sm mt-1">{errors.email}</p>
          )}
          {!errors.email &&
            formData.email &&
            !validateEmail(formData.email) && (
              <p className="text-orange-500 text-xs mt-1">
                Please enter a valid email format
              </p>
            )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone" className="text-[#2C3E50]">
          Phone Number *
        </Label>
        <div className="relative">
          <div className="flex items-center gap-2 border border-[#7F8C8D]/30 rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-[#C0392B] focus-within:border-[#C0392B] transition-all">
            <Select
              value={formData.countryCode}
              onValueChange={handleCountryCodeChange}
            >
              <SelectTrigger className="w-[100px] sm:w-[120px] border-0 focus:ring-0 focus:ring-offset-0 bg-gray-50 border-r border-[#7F8C8D]/30">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {countryCodes.map((item) => (
                  <SelectItem key={item.code} value={item.code}>
                    {item.code} {item.country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              id="phone"
              name="phone"
              type="tel"
              placeholder="123-456-7890"
              value={formData.phone}
              onChange={handleChange}
              maxLength="14"
              className={`flex-1 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 ${
                errors.phone
                  ? "text-red-500"
                  : formData.phone && validatePhone(formData.phone)
                    ? "text-green-600"
                    : ""
              }`}
            />
            {formData.phone &&
              validatePhone(formData.phone) &&
              !errors.phone && <span className="pr-3 text-green-500">✓</span>}
          </div>
        </div>
        {errors.phone && (
          <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
        )}
        {!errors.phone && (
          <p className="text-xs text-[#2C3E50] mt-1">
            Format: {formatPhoneExample()}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="description" className="text-[#2C3E50]">
          How can we help you? *
        </Label>
        <Textarea
          id="description"
          name="description"
          placeholder="Tell us about your trademark or legal service needs..."
          value={formData.description}
          onChange={handleChange}
          rows={4}
          className={`border-[#7F8C8D]/30 focus:border-[#C0392B] resize-none ${errors.description ? "border-red-500" : ""}`}
        />
        {errors.description && (
          <p className="text-red-500 text-sm">{errors.description}</p>
        )}
      </div>

      {/* Submit Button */}
      <div className="pt-2">
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-[#C0392B] hover:bg-[#A93226] text-white font-semibold py-3 px-6 rounded-md transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg"
        >
          {isLoading ? "Submitting..." : submitText}
        </button>
      </div>
    </form>
  );
};

export default ContactForm;
